import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import { getTransactionStatus } from "./TransactionIdSlice";

const baseUrl = process.env.REACT_APP_BASE_URL;

// the payment is already decided: success or failed
const FINAL_STATUSES = ["PAID", "PROCESSING", "FAILED", "REJECTED"];
const isFinalStatus = (status) => FINAL_STATUSES.includes(String(status || "").toUpperCase());

// Auto refund is on when the get-session api sends configs.autoRefundEnabled: true.
// When it is false or missing, checkout behaves as it did before.
export const selectIsAutoRefundEnabled = (state) =>
  state.paymentSessionDetails.sessionDetails?.configs?.autoRefundEnabled === true;

// Called once when a payment flow ends and the payment is not final (the QR timer is over, ...).
// It starts the auto refund window on the backend. Response: { token, transactionId, timerStarted,
// autoRefundAt }, where autoRefundAt is the time the window ends. A repeat call for the same
// transaction is safe: the backend returns the same autoRefundAt.
export const startAutoRefundTimer = createAsyncThunk(
  "autoRefund/startAutoRefundTimer",
  async ({ token, transactionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${token}/transactions/${transactionId}/auto-refund-timer`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    // nothing is sent when the feature is off
    condition: ({ token, transactionId }, { getState }) =>
      selectIsAutoRefundEnabled(getState()) && !!token && !!transactionId,
  }
);

const initialState = {
  // the auto refund loader is visible while this is set. It also blocks the checkout while the timer api is called.
  activeTransactionId: null,
  // when the auto refund window ends, from the timer api
  autoRefundAt: null,
  // the window ended without a success and the shopper closed the refund message
  unresolvedTransactionId: null,
  // the timer api answered timerStarted: false, so it is not called again for this payment
  skippedTransactionId: null,
};

const autoRefundSlice = createSlice({
  name: "autoRefund",
  initialState,
  reducers: {
    hideAutoRefundLoader(state) {
      state.activeTransactionId = null;
      state.autoRefundAt = null;
    },
    closeAutoRefundUnresolved(state) {
      state.unresolvedTransactionId = state.activeTransactionId;
      state.activeTransactionId = null;
      state.autoRefundAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startAutoRefundTimer.pending, (state, action) => {
        state.activeTransactionId = action.meta.arg.transactionId;
        state.autoRefundAt = null;
      })
      .addCase(startAutoRefundTimer.fulfilled, (state, action) => {
        // timerStarted: false means the checkout behaves as it did before
        if (action.payload?.timerStarted === true && action.payload?.autoRefundAt) {
          state.autoRefundAt = action.payload.autoRefundAt;
        } else {
          state.activeTransactionId = null;
          state.skippedTransactionId = action.meta.arg.transactionId;
        }
      })
      .addCase(startAutoRefundTimer.rejected, (state) => {
        state.activeTransactionId = null;
        state.autoRefundAt = null;
      });
  },
});

export const { hideAutoRefundLoader, closeAutoRefundUnresolved } = autoRefundSlice.actions;

const isAutoRefundStarted = (result) =>
  startAutoRefundTimer.fulfilled.match(result) &&
  result.payload?.timerStarted === true &&
  !!result.payload?.autoRefundAt;


export const startAutoRefund =
  (explicitTransactionId, { checkStatusFirst = false } = {}) =>
    async (dispatch, getState) => {
      const state = getState();
      // the iframe / SDK popup keeps the existing behaviour for now
      const isIframe = window.self !== window.top;
      const isIframePopUp = window.opener && window.opener !== window && window.opener.self !== window.opener.top;
      if (isIframe || isIframePopUp) {
        return false;
      }
      // already running, e.g. the same flow ended twice
      if (state.autoRefund.activeTransactionId) {
        return true;
      }
      const transactionId = explicitTransactionId || state.paymentInitiation.paymentInitiationData?.transactionId;
      const token = state.paymentSessionDetails.sessionDetails?.token;
      // The backend already said timerStarted: false, or the window already ran for this payment.
      // Asking again would not change the answer.
      if (
        transactionId &&
        (state.autoRefund.skippedTransactionId === transactionId ||
          state.autoRefund.unresolvedTransactionId === transactionId)
      ) {
        return false;
      }
      if (checkStatusFirst && selectIsAutoRefundEnabled(state) && token && transactionId) {
        const statusResult = await dispatch(getTransactionStatus({ token, transactionId }));
        if (isFinalStatus(statusResult?.payload?.status)) {
          // the existing code shows the success or failed result
          return false;
        }
        // another event may have started it while the status was being checked
        if (getState().autoRefund.activeTransactionId) {
          return true;
        }
      }
      const result = await dispatch(startAutoRefundTimer({ token, transactionId }));
      return isAutoRefundStarted(result);
    };

export default autoRefundSlice.reducer;
